import { useEffect, useState } from "react";
import { getAuthToken } from "../../../lib/auth";

type AndroidShareFile = { name?: string; mime?: string; base64?: string };
export type AndroidSharePayload = { text?: string; files: AndroidShareFile[] };

// Structural subsets of ChatLayout's types — only what this hook reads.
type SpaceLike = { id: string; homeChannelId?: string | null };
type ChannelLike = { id: string };

// Local copy of ChatLayout's pure helper — module-level so effect deps stay stable.
const normalizeChannelId = (chan: string) => (chan.includes(":") ? chan.split(":").slice(-1)[0] : chan);

type UseAndroidShareOptions = {
  currentVoidId: string;
  currentChannelId: string;
  voids: SpaceLike[];
  channels: ChannelLike[];
  /** Append shared text to the composer draft. Must be referentially stable (useCallback). */
  appendText: (text: string) => void;
  /** Queue shared files as pending uploads. */
  addUploads: (files: File[], token: string) => Promise<unknown>;
};

/**
 * Handles share intents from the Android wrapper: the `echo:android-share`
 * window event opens a space/channel picker; once the user confirms and
 * navigation lands on the chosen channel, the shared text/files are delivered
 * into the composer.
 */
export function useAndroidShare({
  currentVoidId,
  currentChannelId,
  voids,
  channels,
  appendText,
  addUploads,
}: UseAndroidShareOptions) {
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<AndroidSharePayload | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [pending, setPending] = useState<{
    spaceId: string;
    channelId: string;
    payload: AndroidSharePayload;
  } | null>(null);

  // Native share intent arrives as a window event from the Android wrapper
  useEffect(() => {
    function onAndroidShare(ev: Event) {
      const detail = (ev as CustomEvent<{ text?: string; files?: AndroidShareFile[] }>).detail;
      if (!detail) return;
      const files = Array.isArray(detail.files) ? detail.files : [];
      const text = detail.text || "";
      setPayload({ text, files });
      setOpen(true);
      setSpaceId(currentVoidId || voids[0]?.id || null);
      setChannelId(currentChannelId || "general");
    }
    window.addEventListener("echo:android-share", onAndroidShare as EventListener);
    return () => window.removeEventListener("echo:android-share", onAndroidShare as EventListener);
  }, [currentVoidId, currentChannelId, voids]);

  // Default the channel to the space's home channel once its channels are loaded
  useEffect(() => {
    if (!open || !spaceId) return;
    if (spaceId !== currentVoidId) return;
    const spaceMeta = voids.find((v) => v.id === spaceId);
    const home = spaceMeta?.homeChannelId ? normalizeChannelId(spaceMeta.homeChannelId) : null;
    if (channelId) return;
    if (home) setChannelId(home);
    else if (channels.length > 0) setChannelId(channels[0].id);
  }, [open, spaceId, channelId, currentVoidId, channels, voids]);

  // Deliver the shared text/files once navigation lands on the chosen channel
  useEffect(() => {
    if (!pending) return;
    const { spaceId: sid, channelId: cid, payload: p } = pending;
    if (currentVoidId !== sid || currentChannelId !== cid) return;
    (async () => {
      try {
        if (p.text) appendText(p.text);
        const files = p.files
          .map((item) => {
            if (!item?.base64) return null;
            const mime = item.mime || "application/octet-stream";
            const name = item.name || "shared";
            const byteStr = atob(item.base64);
            const bytes = new Uint8Array(byteStr.length);
            for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
            const blob = new Blob([bytes], { type: mime });
            return new File([blob], name, { type: mime });
          })
          .filter(Boolean) as File[];
        if (files.length > 0) {
          const tok = getAuthToken() || "";
          await addUploads(files, tok);
        }
      } finally {
        setPending(null);
        setPayload(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- appendText must be stable (documented above); matches pre-extraction deps
  }, [pending, currentVoidId, currentChannelId, addUploads]);

  const cancel = () => {
    setOpen(false);
    setPayload(null);
  };

  /** Select a space in the picker; resets the channel choice. */
  const selectSpace = (next: string | null) => {
    setSpaceId(next);
    setChannelId(null);
  };

  /** Close the picker and queue delivery. Returns the target, or null if incomplete. */
  const confirm = (): { spaceId: string; channelId: string } | null => {
    if (!spaceId || !channelId || !payload) return null;
    setOpen(false);
    setPending({ spaceId, channelId, payload });
    return { spaceId, channelId };
  };

  return { open, payload, spaceId, channelId, selectSpace, setChannelId, cancel, confirm };
}


